import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { reducedMotion } from "@/components/motion";

const GREETING = "Hey Masterpiece";

const EXIT_AT = 2400; // 0.4s black beat + 1.2s entry + 0.8s hold (20% earlier exit)
const DONE_AT = 2880; // overlay removed after the 0.48s exit; hero stagger starts +0.2s

export default function Intro({ onDone }) {
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    if (reducedMotion()) {
      onDone();
      return;
    }
    const t1 = setTimeout(() => setExiting(true), EXIT_AT);
    const t2 = setTimeout(onDone, DONE_AT);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [onDone]);

  const skip = () => onDone();

  return (
    <motion.div
      data-testid="intro-overlay"
      className="fixed inset-0 z-[100] cursor-pointer overflow-hidden bg-black select-none"
      onClick={skip}
    >
      {/* dim starfield + shooting stars around the text */}
      {!exiting && (
        <>
          {[
            { left: "18%", top: "24%", d: 2.2, dur: 3.4 },
            { left: "78%", top: "20%", d: 0.8, dur: 2.8 },
            { left: "12%", top: "62%", d: 1.4, dur: 3.8 },
            { left: "84%", top: "66%", d: 2.8, dur: 3.1 },
            { left: "30%", top: "14%", d: 1.9, dur: 4.2 },
            { left: "66%", top: "80%", d: 0.4, dur: 3.6 },
            { left: "44%", top: "86%", d: 2.5, dur: 2.9 },
          ].map((s, i) => (
            <span key={i} className="intro-star" style={{ left: s.left, top: s.top, ["--tw-del"]: `${s.d}s`, ["--tw-dur"]: `${s.dur}s` }} />
          ))}
          <span className="shooting-star" style={{ left: "10%", top: "20%", ["--ss-del"]: "1.9s" }} />
          <span className="shooting-star" style={{ left: "52%", top: "58%", ["--ss-del"]: "2.6s" }} />
          <span className="shooting-star" style={{ left: "26%", top: "36%", ["--ss-del"]: "3.15s", width: "90px" }} />
        </>
      )}
      <div className="relative z-10 flex h-full flex-col items-center justify-center px-6">
        <motion.span
          data-testid="intro-greeting"
          className="text-center font-display font-semibold whitespace-nowrap text-[clamp(2.4rem,11vw,3rem)] sm:text-[4.95rem] md:text-[6rem]"
          style={{ color: "#E63946", letterSpacing: "0.02em", willChange: "opacity, transform" }}
          initial={{ opacity: 0, scale: 0.94 }}
          animate={exiting ? { opacity: 0, scale: 1.06 } : { opacity: 1, scale: 1 }}
          transition={
            exiting
              ? { duration: 0.48, ease: "easeIn" }
              : { duration: 1.2, delay: 0.4, ease: [0.22, 1, 0.36, 1] }
          }
        >
          {GREETING}
        </motion.span>
      </div>
      <button
        type="button"
        data-testid="intro-skip-button"
        onClick={(e) => {
          e.stopPropagation();
          skip();
        }}
        className="absolute right-7 bottom-7 z-20 text-[11px] tracking-[0.25em] text-white/50 uppercase transition-colors duration-300 hover:text-white/80"
      >
        Skip
      </button>
    </motion.div>
  );
}
